import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from "@angular/core";
import { MatTooltipModule } from "@angular/material/tooltip";
import { TranslateModule } from "@ngx-translate/core";
import { PollInTeam, PollListItem, PollOccurrence } from "app/frontend/lib/polls/user-polls/user-polls.type";
import { UserPollsService } from "../user-polls.service";
import { UserTeam } from "@teammood/shared/util/user";
import { EMPTY, Subject, catchError, filter, mergeMap, takeUntil, tap } from "rxjs";
import { PollSetupService } from "../../polls-setup.service";
import { SnackBarService } from "@teammood/shared/ui/snack-bar";
import { TaskBookBacklogDialogService } from "app/frontend/lib/taskbook-backlog/taskbook-backlog-dialog.service";


@Component({
    selector: "user-poll-occurrence",
    templateUrl: "./user-poll-occurrence.component.html",
    styleUrls: ["./../user-polls.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CommonModule,
        MatTooltipModule,
        TranslateModule,
    ],
})
export class UserPollOccurenceComponent {

    @Input() occurrence: PollOccurrence
    @Input() isSelected: boolean;
    @Input() poll: PollListItem;
    @Input() teams: UserTeam[];
    
    @Output() deletePoll = new EventEmitter<PollOccurrence>();
    
    constructor(
        private userPollsService: UserPollsService,
        private pollSetupService: PollSetupService,
        private snackBarService: SnackBarService,
        private taskbookBacklogDialogService: TaskBookBacklogDialogService
    ){}

    private _destroy$ = new Subject<void>();

    protected async sendReminder(poll: PollListItem){ {
        const dialogRef = await this.userPollsService.openSendPollReminderDialog(poll);
        dialogRef.beforeClosed().pipe(
            filter(data => !!data),
            mergeMap(data => {
                const pollIds = poll.occurrences.flatMap(oc => oc.teams?.map(p => p.pollId));
                if (pollIds?.length > 0) {
                    return this.pollSetupService.sendPollReminder(pollIds).pipe(
                        catchError((err) => {
                            this.snackBarService.error(JSON.stringify(err));
                            throw err;
                        }),
                        tap(() => {
                            this.snackBarService.success('polls_settings.reminder_dialog.success');
                        })
                    );
                } else {
                    return EMPTY;
                }
            }), takeUntil(this._destroy$)).subscribe();
        }
    }

    protected async showPollResultDialog(poll: PollListItem, occurrenceId: string){
        const pollDialogRef = await this.taskbookBacklogDialogService.pollTaskDetails(poll.id, this.teams, 'setup', 'results', occurrenceId);
        pollDialogRef.beforeClosed().pipe(
            takeUntil(pollDialogRef.afterClosed())
        ).subscribe();
    }
    
    protected async remove(poll: PollOccurrence){
        this.deletePoll.emit(poll);
    }

    protected getFrequencyStartDay(startDate: Date, type: string){
        return this.userPollsService.getFrequencyStartDay(startDate, type);
    }

    protected getOccurrenceDate(occurrence: PollOccurrence, type: string){
        return this.userPollsService.getOccurrenceDate(occurrence, type);
    }

    protected getOccurrenceDateDetail(occurrence: PollOccurrence){
        return this.userPollsService.getOccurrenceDateDetail(occurrence);
    }

    protected getOccurrenceParticipation(teams: PollInTeam[]){
        return this.userPollsService.getOccurrenceParticipation(teams);
    }

    protected getLiveOccurrenceDateDetail(occurrence: PollOccurrence){
        return this.userPollsService.getLiveOccurrenceDateDetail(occurrence);
    }

}